import type { ApiClient } from '@momonos/api-client'
import { formatBdt, type Order } from '@momonos/shared'
import { Card, Loader, StatusBadge, useToast } from '@momonos/ui'
import { useEffect, useState } from 'react'

/** FR-ADM-3 — orders, with search. */
export function OrdersScreen({ api, branchId }: { api: ApiClient; branchId: string | null }) {
  const { show } = useToast()
  const [orders, setOrders] = useState<Order[] | null>(null)
  const [query, setQuery] = useState('')

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        // Two characters is the server's minimum; below that show the open board.
        const path = query.trim().length >= 2 ? '/v1/orders/search' : '/v1/orders'
        const result = await api.get<{ orders: Order[] }>(path, {
          query: {
            branch: branchId ?? undefined,
            q: query.trim().length >= 2 ? query.trim() : undefined,
            status: query.trim().length >= 2 ? undefined : 'open',
          },
        })
        if (!cancelled) setOrders(result.orders)
      } catch {
        if (!cancelled) show('Could not load orders', { tone: 'error' })
      }
    }

    // Debounced so typing a phone number is one request, not eleven.
    const timer = setTimeout(load, query ? 300 : 0)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [api, branchId, query, show])

  return (
    <div className="space-y-4">
      <h1 className="font-bold text-2xl text-content">Orders</h1>

      <input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search by order number or phone"
        aria-label="Search orders"
        className="h-12 w-full max-w-md rounded-lg border border-surface-border bg-surface-raised px-3 text-content"
      />

      {!orders ? (
        <Loader label="Loading orders" />
      ) : orders.length === 0 ? (
        <p className="text-content-muted">
          {query.trim().length >= 2 ? 'No orders match that search.' : 'No open orders right now.'}
        </p>
      ) : (
        <div className="grid gap-3">
          {orders.map((order) => (
            <Card key={order.id} className="flex items-center gap-4">
              <div className="flex-1">
                <p className="font-semibold text-content tabular">{order.orderNumber}</p>
                <p className="text-content-muted text-sm">
                  {order.channel}
                  {order.tableLabel && ` · Table ${order.tableLabel}`} ·{' '}
                  {order.items.reduce((count, item) => count + item.quantity, 0)} items
                </p>
              </div>

              <StatusBadge status={order.status} />
              <span className="w-24 text-right font-medium text-content tabular">
                {formatBdt(order.total)}
              </span>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
